import { useEffect, useMemo, useState } from 'react';

import { toast } from 'sonner';

import { defaultCountingKey, mapCountingToEvent, mapDeltaTypeToCounting } from './procedural-helpers';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { CreateProceduralEventInput, useCreateProceduralEvent } from '@/api/mutations/useCreateProceduralEvent';
import { UpdateProceduralEventInput, useUpdateProceduralEvent } from '@/api/mutations/useUpdateProceduralEvent';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { SearchableCombobox } from '@/new/components/searchable-combobox';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';
import { Textarea } from '@/new/components/shadcn/textarea';
import {
  combineDateAndTime,
  instantToCivilUtc,
  splitDateAndTime,
} from '@/new/components/timeline-calculator/civil-date';
import { CivilDatePicker } from '@/new/components/timeline-calculator/civil-date-picker';
import { CountingReference } from '@/new/components/timeline-calculator/counting-reference';
import { EmbeddedCalculator } from '@/new/components/timeline-calculator/embedded-calculator';
import { detectTimeZone, getTimeZoneOptions } from '@/new/lib/time-zones';
import { COUNTING_OPTIONS, CountingKey, useTimelineCalculatorStore } from '@/new/stores/timeline-calculator-store';

interface EventDialogProps {
  /** `null` opens the dialog in create mode. */
  event: ProceduralTimelineEvent | null;
  eventsById: Record<string, ProceduralTimelineEvent>;
  matterId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DAYS_LIMIT = 365;

export function EventDialog({ event, eventsById, matterId, open, onOpenChange }: EventDialogProps) {
  const isEdit = event !== null;
  const createMutation = useCreateProceduralEvent();
  const updateMutation = useUpdateProceduralEvent();
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState('');
  const [anchorEventId, setAnchorEventId] = useState('');
  const [deltaDays, setDeltaDays] = useState('');
  const [countingKey, setCountingKey] = useState<CountingKey>(() => defaultCountingKey());
  const [isConfirmed, setIsConfirmed] = useState(true);
  const [recalculateDependents, setRecalculateDependents] = useState(true);
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  const [isUpdateConfirmOpen, setIsUpdateConfirmOpen] = useState(false);
  const [pendingShared, setPendingShared] = useState<CreateProceduralEventInput | null>(null);
  const closeFloatingCalculator = useTimelineCalculatorStore((s) => s.close);

  useEffect(() => {
    if (!open) return;
    setIsCalcOpen(false);
    setIsUpdateConfirmOpen(false);
    setRecalculateDependents(true);
    closeFloatingCalculator();
    if (event !== null) {
      setName(event.name);
      setDescription(event.description ?? '');
      setTimeZone(event.timeZone ?? '');
      const start = splitDateAndTime(event.startDateTime);
      setStartDate(start.date);
      setStartTime(start.time);
      const end = splitDateAndTime(event.endDateTime);
      setEndDate(end.date);
      setEndTime(end.time);
      setAnchorEventId(event.anchorEventId ?? '');
      setDeltaDays(event.deltaInterval?.days !== undefined ? String(event.deltaInterval.days) : '');
      setCountingKey(mapDeltaTypeToCounting(event.deltaIntervalType, event.jurisdiction));
      setIsConfirmed(event.isConfirmed);
    } else {
      setName('');
      setDescription('');
      setTimeZone(detectTimeZone());
      setStartDate(null);
      setStartTime('');
      setEndDate(null);
      setEndTime('');
      setAnchorEventId('');
      setDeltaDays('');
      setCountingKey(defaultCountingKey());
      setIsConfirmed(true);
    }
  }, [open, event, closeFloatingCalculator]);

  const cycleCreatingIds = useMemo(() => {
    // Filter out any event that would create a (breaking) cycle.
    if (!event) return new Set<string>();
    const descendants = new Set<string>();
    const queue = [event.id];
    while (queue.length) {
      const current = queue.shift()!;
      for (const e of Object.values(eventsById)) {
        if (e.anchorEventId === current && !descendants.has(e.id)) {
          descendants.add(e.id);
          queue.push(e.id);
        }
      }
    }
    return descendants;
  }, [eventsById, event?.id]);

  const anchorOptions = useMemo(() => {
    return Object.values(eventsById)
      .filter((e) => e.id !== event?.id && !cycleCreatingIds.has(e.id))
      .map((e) => ({ value: e.id, label: e.name }));
  }, [eventsById, event?.id, cycleCreatingIds]);

  // How many other events have their date calculated from the one being edited.
  const dependentCount = useMemo(
    () => (event === null ? 0 : Object.values(eventsById).filter((e) => e.anchorEventId === event.id).length),
    [eventsById, event],
  );
  const hasDependents = dependentCount > 0;

  // The event's start date as it was before any edits in this dialog, for
  // comparison against the current form value.
  const originalStartIso = useMemo(() => {
    if (event === null) return undefined;
    const { date, time } = splitDateAndTime(event.startDateTime);
    return date !== null ? combineDateAndTime(date, time) : undefined;
  }, [event]);
  const currentStartIso = startDate !== null ? combineDateAndTime(startDate, startTime) : undefined;
  const startDateChanged = currentStartIso !== originalStartIso;

  // Warn permanently whenever editing an event others depend on.
  const showDependentWarning = isEdit && hasDependents;
  // Offer the cascade toggle only once the start date has actually moved.
  const showDependentToggle = showDependentWarning && startDateChanged;

  // Keep the current zone selectable even if it falls outside the curated list
  // (e.g. an AI-extracted or previously-saved zone).
  const timezoneOptions = useMemo(() => getTimeZoneOptions(timeZone), [timeZone]);

  // Validate the form and build the shared payload, or toast and return null.
  const buildSharedInput = (): CreateProceduralEventInput | null => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Name is required.');
      return null;
    }
    if (!isEdit && startDate === null) {
      toast.error('Start date is required.');
      return null;
    }
    if (!isEdit && timeZone === '') {
      toast.error('Time zone is required.');
      return null;
    }

    const anchored = anchorEventId !== '';
    const parsedDays = parseDaysOrNull(deltaDays);
    if (anchored && parsedDays === null) {
      toast.error('Enter how many days after the dependent event this falls.');
      return null;
    }
    if (parsedDays !== null && !anchored) {
      toast.error('Pick an event for this to depend upon.');
      return null;
    }

    const startIso = startDate !== null ? combineDateAndTime(startDate, startTime) : undefined;
    const endIso = endDate !== null ? combineDateAndTime(endDate, endTime) : undefined;
    if (startIso !== undefined && endIso !== undefined && new Date(endIso) < new Date(startIso)) {
      toast.error('End must be on or after start.');
      return null;
    }

    return {
      name: trimmedName,
      description: description,
      timeZone: timeZone,
      ...(startIso !== undefined ? { startDateTime: startIso } : {}),
      ...(endIso !== undefined ? { endDateTime: endIso } : {}),
      ...(anchored
        ? {
            anchorEventId,
            deltaInterval: { days: parsedDays! },
            ...mapCountingToEvent(countingKey),
          }
        : {}),
      isConfirmed,
    };
  };

  const runCreate = (shared: CreateProceduralEventInput) => {
    createMutation.mutate(
      { matterId, input: shared },
      {
        onSuccess: () => {
          toast.success('Event created.');
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to create event.');
        },
      },
    );
  };

  const runUpdate = (shared: CreateProceduralEventInput) => {
    const input: UpdateProceduralEventInput = { ...shared, recalculateDependents };
    updateMutation.mutate(
      { matterId, eventId: event!.id, input },
      {
        onSuccess: () => {
          toast.success('Event updated.');
          setIsUpdateConfirmOpen(false);
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to update event.');
        },
      },
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const shared = buildSharedInput();
    if (shared === null) return;
    if (!isEdit) {
      runCreate(shared);
      return;
    }
    // Updating an event others are calculated from can shift their dates, so
    // confirm before applying rather than cascading silently.
    if (hasDependents) {
      setPendingShared(shared);
      setIsUpdateConfirmOpen(true);
      return;
    }
    runUpdate(shared);
  };

  const handleConfirmUpdate = () => {
    if (pendingShared !== null) runUpdate(pendingShared);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit event' : 'Create event'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the procedural event details.' : 'Add a new event to the timeline.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          {showDependentWarning && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              <Icon className="lucide--triangle-alert mt-0.5 shrink-0 text-amber-600" />
              <p>
                Other procedural events are dependent on this event. Ensure subsequent events are reviewed upon update.
              </p>
            </div>
          )}
          <div>
            <Label htmlFor="event-name">Event name</Label>
            <Input
              id="event-name"
              name="event-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Defence due"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
            />
          </div>

          <div>
            <Label htmlFor="event-description">Description (optional)</Label>
            <Textarea
              id="event-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional context shown alongside the event."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date{isEdit ? ' (optional)' : ''}</Label>
              <CivilDatePicker value={startDate} onChange={setStartDate} clearable={isEdit} placeholder="Pick a date" />
            </div>
            <div>
              <Label htmlFor="event-start-time">Start time (optional)</Label>
              <Input
                id="event-start-time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={startDate === null}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>End date (optional)</Label>
              <CivilDatePicker
                value={endDate}
                onChange={setEndDate}
                defaultMonth={startDate}
                clearable
                placeholder="Pick a date"
              />
            </div>
            <div>
              <Label htmlFor="event-end-time">End time (optional)</Label>
              <Input
                id="event-end-time"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={endDate === null}
              />
            </div>
          </div>

          <div>
            <Label>Time zone{isEdit ? ' (optional)' : ''}</Label>
            <SearchableCombobox
              options={timezoneOptions}
              value={timeZone}
              onChange={setTimeZone}
              placeholder="Pick a time zone"
              searchPlaceholder="Search time zones"
              emptyText="No matching zones."
              triggerClassName="w-full justify-between font-normal"
              containerClassName="w-full [&>span]:block"
              clearable={isEdit}
            />
          </div>

          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <p className="mb-2 text-xs font-medium text-gray-700">Dependent event (optional)</p>
            <p className="mb-3 text-xs text-gray-500">
              Make this event's date depend upon another event. Pick the event it follows from, how many days later (use
              a negative number for earlier) it falls, and whether weekends and holidays count.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Depends upon</Label>
                <SearchableCombobox
                  options={anchorOptions}
                  value={anchorEventId}
                  onChange={(v) => {
                    setAnchorEventId(v);
                    if (v === '') {
                      setDeltaDays('');
                      setCountingKey(defaultCountingKey());
                    }
                  }}
                  placeholder="None"
                  searchPlaceholder="Search events"
                  emptyText="No matching events."
                  triggerClassName="w-full justify-between font-normal"
                  containerClassName="w-full [&>span]:block"
                />
              </div>
              <div>
                <Label htmlFor="event-delta-days">Days after</Label>
                <Input
                  id="event-delta-days"
                  type="number"
                  min={-DAYS_LIMIT}
                  max={DAYS_LIMIT}
                  value={deltaDays}
                  onChange={(e) => setDeltaDays(e.target.value)}
                  disabled={anchorEventId === ''}
                  placeholder="e.g. 14"
                />
              </div>
              <div>
                <div className="mb-2 flex items-center gap-1.5">
                  <Label className="mb-0">Counting method</Label>
                  <CountingReference side="top" align="end" />
                </div>
                <Select
                  value={countingKey}
                  onValueChange={(v) => setCountingKey(v as CountingKey)}
                  disabled={anchorEventId === ''}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTING_OPTIONS.map((option) => (
                      <SelectItem key={option.key} value={option.key}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              type="button"
              variant="primary"
              onClick={() => setIsCalcOpen(true)}
              disabled={anchorEventId === ''}
              className="mt-3 w-full"
            >
              <Icon className="lucide--calculator" />
              Calculate date
            </Button>
          </div>

          <div className="flex items-center gap-4">
            <Switch id="event-confirmed" checked={isConfirmed} onCheckedChange={setIsConfirmed} />
            <div className="space-y-1">
              <label htmlFor="event-confirmed" className="block cursor-pointer text-sm font-medium text-gray-800">
                Event is confirmed
              </label>
              <p className="text-xs text-gray-500">
                Confirmed dates are finalised. Toggle confirmation if the date depends on an earlier decision in the
                timeline or is likely to change.
              </p>
            </div>
          </div>

          {showDependentToggle && (
            <div className="flex items-center gap-4">
              <Switch
                id="recalc-dependents"
                checked={recalculateDependents}
                onCheckedChange={setRecalculateDependents}
              />
              <div className="space-y-1">
                <label htmlFor="recalc-dependents" className="block cursor-pointer text-sm font-medium text-gray-800">
                  Suggest updates to dependents
                </label>
                <p className="text-xs text-gray-500">
                  Recalculate the dates of events that follow on from this one and surface them as suggestions for
                  review. No events will be directly updated other than the current event.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting && <Icon className="lucide--loader-2 animate-spin" />}
              {isEdit ? 'Save changes' : 'Create event'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <Dialog open={isCalcOpen} onOpenChange={setIsCalcOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Timeline calculator</DialogTitle>
            <DialogDescription>Calculate the deadline from the dependent event's date.</DialogDescription>
          </DialogHeader>
          {anchorEventId !== '' && (
            <EmbeddedCalculator
              matterId={matterId}
              sourceDate={toDateOrNull(eventsById[anchorEventId]?.startDateTime)}
              days={parseDaysOrZero(deltaDays)}
              counting={countingKey}
              onApply={(date) => {
                setStartDate(instantToCivilUtc(date));
                setIsCalcOpen(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={isUpdateConfirmOpen}
        onOpenChange={setIsUpdateConfirmOpen}
        title="Update this event?"
        onConfirm={handleConfirmUpdate}
        isLoading={updateMutation.isLoading}
      >
        <p className="text-sm text-gray-700">
          {dependentCount === 1
            ? 'Another event is calculated from this one.'
            : `${dependentCount} events are calculated from this one.`}
        </p>
        <p className="mt-3 text-sm text-gray-600">
          {recalculateDependents
            ? 'Their dates will be recalculated and proposed as suggestions for you to review. No dependent events are changed directly.'
            : "Their dates won't be recalculated, so they may no longer line up with this change."}
        </p>
      </ConfirmationModal>
    </Dialog>
  );
}

function parseDaysOrNull(value: string): number | null {
  if (value === '' || value === '-') return null;
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return null;
  if (n > DAYS_LIMIT) return DAYS_LIMIT;
  if (n < -DAYS_LIMIT) return -DAYS_LIMIT;
  return n;
}

function parseDaysOrZero(value: string): number {
  return parseDaysOrNull(value) ?? 0;
}

/** `event.startDateTime` is typed as `Date` but arrives as an ISO string over the wire. */
function toDateOrNull(value: Date | string | null | undefined): Date | null {
  if (value == null) return null;
  return value instanceof Date ? value : new Date(value);
}
